import React from 'react';
import { bool, func, number, string } from 'prop-types';
import { Form as FinalForm } from 'react-final-form';
import config from '../../../config';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { Form, InlineTextButton, PrimaryButton } from '../../../components';
import EstimatedCustomerBreakdownMaybe from '../EstimatedCustomerBreakdownMaybe';

import css from './ProductOrderForm.module.css';

const renderForm = formRenderProps => {
  const {
    handleSubmit,
    form: formApi,
    currentStock,
    listingId,
    isOwnListing,
    onFetchTransactionLineItems,
    onContactUser,
    lineItems,
    fetchLineItemsInProgress,
    // fetchLineItemsError,
    values,
    // toggleEnquiryModal,
    // onSubmitModal,
    noShipping,
    profileUpdateInProgress,
    // publicData,
    // pickupEnabled,
  } = formRenderProps;

  // const hasLocation = publicData && publicData.location;
  // const freeShipping = !!noShipping;

  // const handleOnChange = formValues => {
  //   const { quantity: quantityRaw, deliveryMethod } = formValues.values;
  //   const quantity = Number.parseInt(quantityRaw, 10);
  //   const isBrowser = typeof window !== 'undefined';
  //   // if (isBrowser && quantity && deliveryMethod && !fetchLineItemsInProgress) {
  //   // onFetchTransactionLineItems({
  //   //   orderData: { quantity, deliveryMethod: freeShipping ? 'freeShipping' : deliveryMethod },
  //   //   listingId,
  //   //   isOwnListing,
  //   // });
  //   // }
  // };

  // In case quantity and deliveryMethod are missing focus on that select-input.
  // Otherwise continue with the default handleSubmit function.
  const handleFormSubmit = e => {
    const { quantity = 1, deliveryMethod } = values || {};
    if (!quantity || quantity < 1) {
      e.preventDefault();
      // Blur event will show validator message
      formApi.blur('quantity');
      formApi.focus('quantity');
    } else if (!deliveryMethod && !noShipping) {
      e.preventDefault();
      // Blur event will show validator message
      formApi.blur('deliveryMethod');
      formApi.focus('deliveryMethod');
    } else {
      handleSubmit(e);
    }
  };

  const breakdownData = {};

  const breakdown = false ? (
    <div className={css.breakdownWrapper}>
      <h3>
        <FormattedMessage id="ProductOrderForm.breakdownTitle" />
      </h3>
      <EstimatedCustomerBreakdownMaybe
        unitType={config.lineItemUnitType}
        breakdownData={breakdownData}
        lineItems={lineItems}
      />
    </div>
  ) : null;

  const showContactUser = typeof onContactUser === 'function';

  const onClickContactUser = e => {
    e.preventDefault();
    onContactUser();
  };

  const contactSellerLink = (
    <InlineTextButton onClick={onClickContactUser}>
      <FormattedMessage id="ProductOrderForm.finePrintNoStockLinkText" />
    </InlineTextButton>
  );

  const hasStock = currentStock && currentStock > 0;

  const submitInProgress = fetchLineItemsInProgress;
  const submitDisabled = !hasStock;

  return (
    <Form className={css.bookingPanelButtons} onSubmit={handleFormSubmit}>
      {breakdown}
      <div className={css.productOrderButton}>
        <PrimaryButton
          className={css.submitButton}
          type="submit"
          inProgress={profileUpdateInProgress || submitInProgress}
          disabled={submitDisabled}
        >
          {hasStock ? (
            <FormattedMessage id="ProductOrderForm.ctaButton" />
          ) : (
            <FormattedMessage id="ProductOrderForm.ctaButtonNoStock" />
          )}
        </PrimaryButton>
        <PrimaryButton
          className={css.makeOfferButton}
          type="button"
          onClick={() => onContactUser('offer')}
        >
          <FormattedMessage id="ProductOrderForm.makeAnOffer" />
        </PrimaryButton>
      </div>
      <p className={css.finePrint}>
        {hasStock ? null : showContactUser ? (
          <FormattedMessage id="ProductOrderForm.finePrintNoStock" values={{ contactSellerLink }} />
        ) : null}
      </p>
    </Form>
  );
};

const ProductOrderForm = props => {
  const intl = useIntl();
  const {
    price,
    currentStock,
    pickupEnabled,
    shippingEnabled,
    noShipping,
    profileUpdateInProgress,
  } = props;

  // Should not happen for listings that go through EditListingWizard.
  // However, this might happen for imported listings.
  // if (!pickupEnabled && !shippingEnabled) {
  //   return (
  //     <p className={css.error}>
  //       <FormattedMessage id="ProductOrderForm.noDeliveryMethodSet" />
  //     </p>
  //   );
  // }

  if (!price) {
    return (
      <p className={css.error}>
        <FormattedMessage id="ProductOrderForm.listingPriceMissing" />
      </p>
    );
  }
  if (price.currency !== config.currency) {
    return (
      <p className={css.error}>
        <FormattedMessage id="ProductOrderForm.listingCurrencyInvalid" />
      </p>
    );
  }
  const hasOneItemLeft = currentStock && currentStock === 1;
  const quantityMaybe = hasOneItemLeft ? { quantity: '1' } : {};
  const singleDeliveryMethodAvailableMaybe =
    shippingEnabled && !pickupEnabled
      ? { deliveryMethod: 'shipping' }
      : !shippingEnabled && pickupEnabled && !noShipping
      ? { deliveryMethod: 'pickup' }
      : !shippingEnabled && !pickupEnabled
      ? { deliveryMethod: 'noShipping' }
      : noShipping && pickupEnabled
      ? { deliveryMethod: 'noShipping' }
      : shippingEnabled && pickupEnabled
      ? { deliveryMethod: 'shipping' }
      : {};

  const hasMultipleDeliveryMethods = pickupEnabled && shippingEnabled;
  const initialValues = {
    ...quantityMaybe,
    ...singleDeliveryMethodAvailableMaybe,
  };

  return (
    <FinalForm
      initialValues={initialValues}
      hasMultipleDeliveryMethods={hasMultipleDeliveryMethods}
      noShipping={noShipping}
      profileUpdateInProgress={profileUpdateInProgress}
      {...props}
      intl={intl}
      render={renderForm}
      pickupEnabled={pickupEnabled}
    />
  );
};

ProductOrderForm.defaultProps = {
  rootClassName: null,
  className: null,
  price: null,
  currentStock: null,
  listingId: null,
  isOwnListing: false,
  lineItems: null,
  fetchLineItemsError: null,
};

ProductOrderForm.propTypes = {
  rootClassName: string,
  className: string,

  // form
  formId: string.isRequired,
  onSubmit: func.isRequired,

  // listing
  listingId: propTypes.uuid,
  price: propTypes.money,
  currentStock: number,
  isOwnListing: bool,

  // line items
  lineItems: propTypes.lineItems,
  onFetchTransactionLineItems: func.isRequired,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,

  // other
  onContactUser: func,
};

export default ProductOrderForm;
